/* @flow */

import type { RouterHistory as History } from "react-router";
import type { Node as ReactNode } from "react";

import React, { useState, useContext, useRef, useEffect, useMemo } from "react";
import { usePreserveScrollPosition } from "react-router-on-navigation";
import { useTranslate } from "@awardit/react-use-translate";
import { useBrowser } from "@awardit/react-use-browser";
import { NotificationBar } from "@crossroads/ui-components";
import cn from "classnames";
import { Helmet } from "react-helmet-async";
import { StoreInfoContext, useClient } from "entrypoint/shared";
import AppHeader from "components/AppHeader";
import AppFooter from "components/AppFooter";
import Notice from "components/Notice";
import Banner from "components/Banner";
import { useData, useSendMessage } from "crustate/react";
import { useCookieGeneric } from "helpers/use-cookie";
import { getFavicons } from "helpers/get-meta";
import { isShopOrAccountRoute } from "helpers/utils";
import { TransactionsData, DistrictInfoData, TinkProvidersData, CustomerData, CmsData, ViewModeData } from "data";
import { RecruitAFriendModal } from "components/RecruitAFriend";
import TinkProvidersModal from "components/TinkProvidersModal";
import { PREVIEW_PAGESIZE as TRANSACTIONS_PREVIEW_PAGESIZE } from "state/transactions";
import useOnFullMenuHiddenRoute from "helpers/use-on-full-menu-hidden-route";
import { memberTagList, awarditAgreementAgree, customer as customerQuery } from "queries";
import { MODE } from "state/view-mode";
import { logout, syncCustomer } from "@crossroads/shop-state/customer";
import { Title } from "components/UiComponents";
import CookieConsent from "components/CookieConsent";
import Agreement from "components/Agreement";
import useDevice from "helpers/use-device";
import { hasExpiringPoints } from "components/ExpiringPointsBanner";

import styles from "./styles.scss";

type Props = {
  children: ReactNode,
  location: Location,
  history: History,
};

const App = ({ children, location, history }: Props): React$Node => {
  const t = useTranslate();
  const container = useRef(null);
  const isBrowser = useBrowser();
  const isDesktop = useDevice("gt", 1239);
  const sendMessage = useSendMessage();
  const onHomeView = location.pathname === "/" || location.pathname.includes("index.html");
  const { info, configuration, routes, content: { homeview, appheader, transactionsview } } =
    useContext(StoreInfoContext);
  const { value: IEAccepted, setValue: setIEAccepted } =
    useCookieGeneric("ie_notice_accepted", false, { expires: 365 });
  const [subNavOpen, setSubNavOpen] = useState(false);
  const [onIE, setOnIE] = useState(false);

  const onCheckout = (): boolean => {
    if (!routes.checkoutView || !routes.checkoutView.url) {
      return false;
    }

    return location.pathname.includes(`${routes.checkoutView.url}/`);
  };

  const onSuccess = location.pathname === routes.checkoutSuccessView?.url;
  const onAbout = location.pathname.includes("about-");
  const showTink = useMemo(() =>
    (routes.accountTinkView && routes.accountTinkView.toggle) ?? false, []);
  const onFullMenuHiddenRoute = useOnFullMenuHiddenRoute();
  const [hideNotificationBar, setHideNotificationBar] = useState(false);
  const flag = transactionsview.allTransactions === true ? "ALL_INCLUDING_CANCELED_AND_REFUND" : null;
  const customerData = useData(CustomerData);
  const customer = customerData.state === "LOGGED_IN" ? customerData.data : null;
  const showExpiringPoints = !isDesktop && Boolean(
    routes.accountView?.url && hasExpiringPoints(customer)
  );
  const viewMode = useData(ViewModeData);
  const [hasNoOwnPoints, setHasNoOwnPoints] = useState(false);
  const [memberHasNoOwnPointsTag, setMemberHasNoOwnPointsTag] = useState(false);
  const [hasBeenBlocked, setHasBeenBlocked] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const client = useClient();
  const questionnaireViewUrl = routes.questionnaireView && routes.questionnaireView.url ? routes.questionnaireView.url : "";
  const cookieConsentIsVisible = viewMode === MODE.COOKIE_CONSENT;

  const hasMobileSubNav = [
    questionnaireViewUrl,
    questionnaireViewUrl + "/:slug",
  ].some(route => location.pathname.includes(route));

  const hasMobileShopMenu =
    !onCheckout() &&
    !hasMobileSubNav;

  /** Check if the client is using an unsupported browser (IE) */
  useEffect(() => {
    if (window.document.documentMode) {
      setOnIE(true);
    }

    client(memberTagList).then(({ memberTagList }) => {
      if (memberTagList.find(tag => Object.values(tag).includes("No own points"))) {
        setMemberHasNoOwnPointsTag(true);
      }
    });

    // Members who doesn't have any points on
    // their own shouldn't have access to the checkout
    if (
      (customerData.state === "LOGGED_IN" &&
      customerData.data &&
      customerData.data.awardit.ownPoints === 0 &&
      customerData.data.awardit.groupPoints > 0 &&
      customerData.data.awardit.activePoints === 0) ||
      memberHasNoOwnPointsTag
    ) {
      setHasNoOwnPoints(true);
    }

    if (customerData.state === "LOGGED_IN" &&
      customerData.data &&
      customerData.data.memberPoints &&
      customerData.data.memberPoints.redeemBlocked !== null &&
      customerData.data.memberPoints.redeemBlocked !== undefined &&
      customerData.data.memberPoints.redeemBlocked
    ) {
      setHasBeenBlocked(true);
    }
  }, [customerData, client]);

  useEffect(() => {
    if (isBrowser &&
      configuration.showTermsOnFirstLogin === true &&
      customerData.state === "LOGGED_IN" &&
      customerData.data &&
      customerData.data.awardit.agreementAccepted === false) {
      setShowTerms(true);
    }
  }, [isBrowser, customerData, configuration]);

  const postAgreement = async () => {
    const res = await client(awarditAgreementAgree);
    if (res.awarditAgreementAgree === "success") {
      await client(customerQuery).then(({ customer }) => {
        sendMessage(syncCustomer(customer));
      });
      setShowTerms(false);
    }
  };

  usePreserveScrollPosition(history);

  return (
    <TinkProvidersData.Provider load={showTink}>
      <DistrictInfoData.Provider>
        <TransactionsData.Provider name="preview" size={TRANSACTIONS_PREVIEW_PAGESIZE} filter={{ transactionFlag: flag }} lang={info.locale.slice(0, 2)}>
          <CmsData.Provider url="cookie-consent">
            <div
              ref={container} className={cn(
                styles.block,
                { [styles.onCheckout]: onCheckout() },
                { [styles.fullMenu]: !onHomeView && !onFullMenuHiddenRoute },
                { [styles.onAbout]: onAbout },
                { [styles.hasMobileSubNav]: hasMobileSubNav },
                { [styles.hasExpiringPoints]: showExpiringPoints },
                { [styles.hasMobileShopMenu]: hasMobileShopMenu }
              )}
            >
              <Helmet
                titleTemplate={`${configuration.pageTitlePrefix || ""} %s ${configuration.pageTitleSuffix || ""}`}
                link={getFavicons(configuration)}
                htmlAttributes={{ lang: info.locale.slice(0, 2) }}
              />

              <div className={cn(styles.content, "awardit-AppContent")}>
                <AppHeader
                  className={styles.header}
                  subNavOpen={subNavOpen}
                  setSubNavOpen={setSubNavOpen}
                  onHomeView={onHomeView}
                  onCheckout={onCheckout()}
                  onSuccess={onSuccess}
                />
                <div className={styles.height} />

                {location.pathname !== routes.termsView?.url && !showTerms &&
                  <CookieConsent />
                }

                {showTerms && !cookieConsentIsVisible &&
                  <Agreement
                    agreementModalOpen={showTerms}
                    setAgreementModalOpen={open => {
                      if (open === true) {
                        sendMessage(logout());
                      }

                      if (open === false) {
                        postAgreement();
                      }
                    }}
                  />
                }

                <div className={styles.notices}>
                  {onIE && <Notice
                    cookieName="ie_notice_accepted"
                    acceptanceText={t("IE_NOTICE")}
                    accepted={IEAccepted}
                    callback={setIEAccepted}
                  />}
                </div>
                <div className={cn(styles.children, "awardit-children", { "awardit-childrenOnHomeView": onHomeView })}>
                  {hasNoOwnPoints &&
                  isShopOrAccountRoute(location.pathname, routes, onFullMenuHiddenRoute) &&
                    <Banner
                      className={styles.bannerWarning}
                    >
                      <Title elem="h2" className={styles.title}>{appheader.noOwnPointsTitle ?? ""}</Title>
                      <p>{appheader.noOwnPointsBody ?? ""}</p>
                    </Banner>
                  }
                  {hasBeenBlocked &&
                  isShopOrAccountRoute(location.pathname, routes, onFullMenuHiddenRoute) &&
                    <Banner
                      className={styles.bannerWarning}
                    >
                      <p>{t("MESSAGE.BLOCKED")}</p>
                    </Banner>
                  }

                  {children}
                </div>

                {!onCheckout() &&
                  <AppFooter
                    className={styles.footer}
                    setSubNavOpen={setSubNavOpen}
                  />
                }

                {homeview.notificationBarStatus !== null &&
                homeview.notificationBarStatus === true &&
                homeview.notificationBarText !== "" &&
                !hideNotificationBar &&
                  <NotificationBar
                    className={cn(styles.notificationBar, "awardit-notificationBar")}
                    text={homeview.notificationBarText}
                    onClose={() => setHideNotificationBar(true)}
                  />
                }

                <RecruitAFriendModal overlayConfig={{ color: "var(--light)", opacity: 0.9 }} />

                {routes.accountTinkView && routes.accountTinkView.toggle !== undefined &&
                routes.accountTinkView.toggle &&
                  <TinkProvidersModal overlayConfig={{ color: "var(--light)", opacity: 0.9 }} />
                }
              </div>
            </div>
          </CmsData.Provider>
        </TransactionsData.Provider>
      </DistrictInfoData.Provider>
    </TinkProvidersData.Provider>
  );
};

export default App;
